import { getCurrencies } from 'data/Currencies/selectors'
import { ApiCurrency } from 'data/Currencies/types'
import React from 'react'
import { FormattedNumber } from 'react-intl'
import { useSelector } from 'react-redux'

import { fiatSymbols } from '../constants/currency'

type Props = {
  amount: number | string
  currency: string
  currencyData?: ApiCurrency
  // eslint-disable-next-line react/no-unused-prop-types
  fixedDecimals?: number
  // eslint-disable-next-line react/no-unused-prop-types
  fullResolution?: boolean // force use all decimals: 0.1000000 instead of or 0.1
  hideCurrency?: boolean // Show "0.12" instead of "$ 0.12"
  maxDecimals?: number
  // eslint-disable-next-line react/no-unused-prop-types
  minDecimals?: number
}

const getTargetDecimals = ({ currencyData, maxDecimals }: Props) => {
  const decimals = currencyData?.precision ?? 2
  return maxDecimals && decimals > maxDecimals ? maxDecimals : decimals
}

const getMinDecimals = (props: Props) => {
  if (props.fixedDecimals) return props.fixedDecimals
  if (props.minDecimals) return props.minDecimals
  if (!props.fullResolution) return undefined
  return getTargetDecimals(props)
}

const getMaxDecimals = (props: Props) => {
  if (props.fixedDecimals) return props.fixedDecimals
  return getTargetDecimals(props)
}

// Money is used to render plain money amounts, like a balance in a specific currency
// For currency exchange Money amounts please check PairMoney, that considers the pair
// to render specific amount of decimals
export const PureMoney = (props: Props) => {
  const { amount, currency, currencyData, hideCurrency = false, maxDecimals } = props

  let decimals = currencyData?.precision ?? 2
  if (maxDecimals && decimals > maxDecimals) {
    decimals = maxDecimals
  }

  const fiatSymbol = fiatSymbols[currency]
  if (fiatSymbol) {
    return (
      <>
        {!hideCurrency && fiatSymbol}
        <FormattedNumber
          maximumFractionDigits={getMaxDecimals(props)}
          minimumFractionDigits={getMinDecimals(props)}
          value={Number(amount)}
        />
      </>
    )
  }

  return (
    <>
      <FormattedNumber
        maximumFractionDigits={getMaxDecimals(props)}
        minimumFractionDigits={getMinDecimals(props)}
        value={Number(amount)}
      />
      {!hideCurrency && <>&nbsp;{currencyData?.displaySymbol ?? currency}&nbsp;</>}
    </>
  )
}

const Money = (props: Props) => {
  const currencies = useSelector(getCurrencies)
  return <PureMoney {...props} currencyData={currencies.find((c) => c.symbol === props.currency)} />
}

export default Money
